import { useCallback, useEffect, useRef } from 'react';

import { addCustomSpans } from '@atlaskit/react-ufo/custom-spans';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { isInitial } from '@confluence/browser-metrics/entry-points/utils.ts';
import type { AttachmentActionLoadState } from '@confluence/forge-ui/entry-points/useAttachmentActionLoadState.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { getMacroExperienceTracker } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';

export type AttachmentMacroExtensionState = {
	metadata: AttachmentActionLoadState;
	actions: AttachmentActionLoadState;
};

type Measurement = {
	identity: string;
	active: boolean;
	completed: boolean;
	startTime: number;
	clockSource: 'macro-tracker' | 'renderer-mount';
	baseReadyTime?: number;
	attachmentCount: number;
	extensions?: AttachmentMacroExtensionState;
	navigation: 'initial' | 'transition';
	timer?: ReturnType<typeof setTimeout>;
};

const DISABLED_STATE: AttachmentActionLoadState = { status: 'disabled', hasExtensions: false };
const NO_EXTENSIONS = { metadata: DISABLED_STATE, actions: DISABLED_STATE };
const LOAD_TIMEOUT = 60_000;
type Outcome = 'success' | 'failure' | 'access-unavailable' | 'timeout' | 'unmounted';

const getDiscoveryOutcome = (states: AttachmentActionLoadState[]): Outcome => {
	if (states.some(({ status }) => status === 'failure')) return 'failure';
	if (states.some(({ status }) => status === 'access-unavailable')) return 'access-unavailable';
	return 'success';
};

const getAction = (outcome: Outcome): string => {
	if (outcome === 'success') return 'taskSuccess';
	if (outcome === 'failure') return 'taskFail';
	return 'taskAbort';
};

const attachmentCountBucket = (count: number): string => {
	if (count === 0) return '0';
	if (count <= 10) return '1-10';
	if (count <= 100) return '11-100';
	return '101+';
};

/** Observes initial readiness only; pagination and opening a Forge app are separate interactions. */
export const useAttachmentsMacroPerformance = ({
	contentId,
	macroId,
	experienceName,
	mode,
	loading,
	error,
	attachmentCount,
}: {
	contentId: string;
	macroId: string;
	experienceName: string;
	mode: string;
	loading: boolean;
	error: Error | null;
	attachmentCount: number;
}): ((extensions: AttachmentMacroExtensionState) => void) => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const hydrating = useIsSSROrHydrating();
	const identity = `${mode}.${contentId}.${macroId}`;
	const identityRef = useRef(identity);
	identityRef.current = identity;
	const measurementRef = useRef<Measurement>();
	const extensionStateRef = useRef<{
		identity: string;
		extensions: AttachmentMacroExtensionState;
	}>();
	const createEventRef = useRef(createEvent);
	createEventRef.current = createEvent;

	const finish = useCallback((measurement: Measurement, outcome: Outcome): void => {
		if (measurement.completed) return;
		measurement.completed = true;
		clearTimeout(measurement.timer);
		const endTime = performance.now();
		const states = measurement.extensions ? Object.values(measurement.extensions) : [];
		const discoverySucceeded =
			outcome === 'success' &&
			states.length > 0 &&
			states.every(({ status }) => status === 'disabled' || status === 'success');
		if (outcome === 'success') {
			addCustomSpans('attachments-macro/extensions-ready', measurement.startTime, endTime);
		}
		createEventRef
			.current({
				type: 'sendOperationalEvent',
				data: {
					source: 'ui',
					actionSubject: 'macroTracker',
					action: getAction(outcome),
					attributes: {
						task: 'attachments-macro/load',
						extensionKey: 'attachments',
						outcome,
						duration: endTime - measurement.startTime,
						baseReadyDuration:
							measurement.baseReadyTime === undefined
								? undefined
								: measurement.baseReadyTime - measurement.startTime,
						extensionWaitDuration:
							measurement.baseReadyTime === undefined
								? undefined
								: endTime - measurement.baseReadyTime,
						hasApplicableExtensions: discoverySucceeded
							? states.some(({ hasExtensions }) => hasExtensions)
							: null,
						metadataStatus: measurement.extensions?.metadata.status ?? 'not-started',
						actionsStatus: measurement.extensions?.actions.status ?? 'not-started',
						attachmentCountBucket: attachmentCountBucket(measurement.attachmentCount),
						navigation: measurement.navigation,
						clockSource: measurement.clockSource,
					},
				},
			})
			.fire();
	}, []);

	const settle = useCallback(() => {
		const measurement = measurementRef.current;
		if (
			!measurement ||
			measurement.identity !== identity ||
			!measurement.active ||
			measurement.completed ||
			measurement.baseReadyTime === undefined ||
			!measurement.extensions
		)
			return;
		const states = Object.values(measurement.extensions);
		const outcome = getDiscoveryOutcome(states);
		if (outcome === 'success' && states.some(({ status }) => status === 'loading')) return;
		finish(measurement, outcome);
	}, [finish, identity]);

	useEffect(() => {
		if (hydrating || mode !== 'renderer' || !contentId || !macroId) return;
		let measurement = measurementRef.current;
		if (!measurement || measurement.identity !== identity) {
			const startTime = getMacroExperienceTracker(mode, contentId).getExperienceState(
				experienceName,
			)?.startTime;
			measurement = {
				identity,
				active: true,
				completed: false,
				startTime: startTime ?? performance.now(),
				clockSource: startTime === undefined ? 'renderer-mount' : 'macro-tracker',
				attachmentCount: 0,
				navigation: isInitial() ? 'initial' : 'transition',
			};
			measurementRef.current = measurement;
			const pendingMeasurement = measurement;
			measurement.timer = setTimeout(() => finish(pendingMeasurement, 'timeout'), LOAD_TIMEOUT);
		}
		measurement.active = true;
		const activeMeasurement = measurement;
		return () => {
			activeMeasurement.active = false;
			// StrictMode replays effects synchronously. Let a replay reclaim this measurement.
			void Promise.resolve().then(() => {
				if (!activeMeasurement.active) finish(activeMeasurement, 'unmounted');
			});
		};
	}, [contentId, experienceName, finish, hydrating, identity, macroId, mode]);

	useEffect(() => {
		const measurement = measurementRef.current;
		if (!measurement || measurement.identity !== identity || measurement.completed) return;
		measurement.attachmentCount = attachmentCount;
		if (error) {
			finish(measurement, 'failure');
			return;
		}
		if (loading) return;
		if (measurement.baseReadyTime === undefined) {
			measurement.baseReadyTime = performance.now();
			addCustomSpans(
				'attachments-macro/base-ready',
				measurement.startTime,
				measurement.baseReadyTime,
			);
		}
		measurement.extensions =
			attachmentCount === 0
				? NO_EXTENSIONS
				: extensionStateRef.current?.identity === identity
					? extensionStateRef.current.extensions
					: undefined;
		settle();
	}, [attachmentCount, error, finish, hydrating, identity, loading, settle]);

	return useCallback(
		(extensions: AttachmentMacroExtensionState) => {
			if (identityRef.current !== identity) return;
			extensionStateRef.current = { identity, extensions };
			const measurement = measurementRef.current;
			if (measurement?.identity === identity) measurement.extensions = extensions;
			// The parent data effect and all row commits must finish before we publish readiness.
			void Promise.resolve().then(settle);
		},
		[identity, settle],
	);
};
